import ReactGA from "react-ga4";
import mixpanel from "mixpanel-browser";
import { mixpanelToken } from "../../config/envConfig";

const TRACKING_ID = "G-1FXXD7PF3E";

export const UTM_SOURCES = {
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
  LINKEDIN: 'linkedin',
  TWITTER: 'twitter',
  GOOGLE: 'google',
  EMAIL: 'email',
  NEWSLETTER: 'newsletter',
  DIRECT: 'direct',
  REFERRAL: 'referral',
  PARTNER: 'partner',
  YOUTUBE: 'youtube',
  WHATSAPP: 'whatsapp',
  TELEGRAM: 'telegram'
} as const;

export const initAnalytics = () => {
  ReactGA.initialize(TRACKING_ID);

  mixpanel.init(mixpanelToken, {
    debug: !process.env.NODE_ENV || process.env.NODE_ENV === 'development',
    track_pageview: false,
    persistence: 'localStorage',
    autotrack: false
  });

  trackUtmParameters();
};

export const logPageView = (utmSource?: string) => {
  const pageData = {
    page: window.location.pathname + window.location.search,
    fullUrl: window.location.href,
    timestamp: new Date().toISOString(),
    environment: process.env.NODE_ENV,
    page_title: document.title,
    current_domain: window.location.hostname,
    current_url_path: window.location.pathname,
    current_url_protocol: window.location.protocol,
    current_url_search: window.location.search,
    ...(utmSource && { utm_source: utmSource })
  };
   
  try {
    ReactGA.send({
      hitType: "pageview",
      ...pageData
    });
    mixpanel.track(`${pageData.current_url_path} : Page Viewed`, pageData);
  } catch (error) {
    console.error('Analytics Page View Error:', error);
  }
};

export const trackUtmParameters = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const utmSource = searchParams.get('utm_source');

  if (utmSource && Object.values(UTM_SOURCES).includes(utmSource as any)) {
    logPageView(utmSource);
    
    try {
      ReactGA.event({
        category: 'Campaign',
        action: 'UTM Visit',
        label: utmSource
      });
    } catch (error) {
      console.error('Analytics UTM Tracking Error:', error);
    }
  }
};

export const logEvent = (category = "", action = "", label = "", properties = {}) => {
  if (category && action) {
    // Google Analytics event
    ReactGA.event({
      category,
      action,
      label,
      ...properties
    });

    // Enhanced Mixpanel tracking
    if (action === 'Checkout Attempt') {
      // Use structured properties directly instead of parsing
      mixpanel.track(action, {
        category,
        event_type: action,
        ...properties,  // Use the properties passed directly
        timestamp: new Date().toISOString(),
        environment: process.env.NODE_ENV
      });
    } else {
      // Default tracking for other events
      mixpanel.track(action, {
        category,
        label,
        ...properties
      });
    }
  }
};

export const logException = (description = "", fatal = false, error?: Error) => {
  if (description) {
    const errorProperties = {
      description,
      fatal,
      timestamp: new Date().toISOString(),
      errorMessage: error?.message,
      errorStack: error?.stack,
      errorName: error?.name
    };

    ReactGA.event({
      category: 'Error',
      action: 'Exception',
      ...errorProperties
    });

    mixpanel.track('Exception', errorProperties);
  }
};

// MixPanel User Login Tracking
export const logUserLogin = (userData: {
  userId: string;
  email: string;
  name?: string;
  user_role: string;
  mobile_number: string;
  first_name: string;
  last_name: string;
  educational_info?: {
    class?: string;
  };
}) => {
  try {
    mixpanel.identify(userData.userId);

    mixpanel.people.set({
      $email: userData.email,
      $name: userData.name,
      $last_login: new Date().toISOString(),
      user_role: userData.user_role,
      mobile_number: userData.mobile_number,
      first_name: userData.first_name,
      last_name: userData.last_name,
      educational_class: userData.educational_info?.class
    });

    mixpanel.track('User Login', {
      userId: userData.userId,
      email: userData.email,
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV,
      user_role: userData.user_role,
      mobile_number: userData.mobile_number,
      first_name: userData.first_name,
      last_name: userData.last_name,
      educational_class: userData.educational_info?.class
    });
  } catch (error) {
    console.error('Mixpanel User Login Tracking Error:', error);
  }
};
